import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Check } from "lucide-react";
import { BRAND, WELCOME_MESSAGE } from "@/constants/compliance";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const PhoneMockup = () => (
  <div
    data-testid="hero-phone-mockup"
    className="relative w-[290px] sm:w-[320px] rounded-[2.6rem] bg-[#1E2022] p-2.5 shadow-[0_40px_80px_rgba(30,32,34,0.25)]"
  >
    <div className="rounded-[2.1rem] bg-[#FAF8F5] overflow-hidden">
      <div className="flex justify-center pt-3 pb-2">
        <div className="w-24 h-6 bg-[#1E2022] rounded-full" />
      </div>
      <div className="px-4 pt-2 pb-6 min-h-[430px] flex flex-col">
        <div className="text-center mb-4">
          <p className="font-mono2 text-[10px] tracking-[0.2em] text-[#8A8F98] uppercase">
            {BRAND.number}
          </p>
          <p className="text-[10px] text-[#8A8F98]">Today 9:41 AM</p>
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 1.5, duration: 0.5, ease: "easeOut" }}
          className="self-end max-w-[75%] bg-[#D96B43] text-white rounded-2xl rounded-br-md px-4 py-2.5 mb-3"
        >
          <p className="font-mono2 text-sm font-semibold tracking-widest">START</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 2.3, duration: 0.5, ease: "easeOut" }}
          className="self-start max-w-[88%] bg-white border border-stone-200 rounded-2xl rounded-bl-md px-4 py-3 mb-3 shadow-sm"
        >
          <p className="text-[11px] leading-relaxed text-[#575B60]">{WELCOME_MESSAGE}</p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 3.2, duration: 0.5, ease: "easeOut" }}
          className="self-start max-w-[85%] bg-[#EAF1EC] border border-[#4A7C59]/20 rounded-2xl rounded-bl-md px-4 py-3"
        >
          <p className="text-xs font-medium text-[#1E2022]">
            Reminder: Buy groceries @ 5 PM
          </p>
          <p className="text-[10px] text-[#4A7C59] mt-1 flex items-center gap-1">
            <Check size={11} /> On your list
          </p>
        </motion.div>
      </div>
    </div>
  </div>
);

export const KineticHero = () => {
  const { scrollY } = useScroll();
  const phoneY = useTransform(scrollY, [0, 700], [0, -70]);
  const blobY = useTransform(scrollY, [0, 700], [0, 120]);

  const scrollToOptin = () => {
    const el = document.querySelector("#optin");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden grain bg-[#FAF8F5] pt-24 pb-16"
    >
      <motion.div
        style={{ y: blobY }}
        className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full bg-[#E89F8A]/25 blur-3xl pointer-events-none"
      />
      <motion.div
        style={{ y: blobY }}
        className="absolute bottom-0 -left-40 w-[480px] h-[480px] rounded-full bg-[#4A7C59]/15 blur-3xl pointer-events-none"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-8 items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="font-mono2 text-xs sm:text-sm tracking-[0.25em] uppercase text-[#4A7C59] mb-6"
            data-testid="hero-eyebrow"
          >
            SMS task reminders · no app required
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.04] text-5xl sm:text-6xl lg:text-7xl text-[#1E2022]"
          >
            <MaskedLine delay={0.35}>Never forget</MaskedLine>
            <MaskedLine delay={0.5}>
              the <span className="italic text-[#D96B43]">small stuff</span>
            </MaskedLine>
            <MaskedLine delay={0.65}>ever again.</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-7 max-w-md text-base sm:text-lg text-[#575B60] leading-relaxed"
            data-testid="hero-subcopy"
          >
            {BRAND.name} sends gentle, automated text-message reminders for the
            tasks that run your day. One keyword to start. One word to stop.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.15, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-start"
              onClick={scrollToOptin}
              className="group font-display font-bold bg-[#D96B43] text-white rounded-full px-7 py-4 text-base hover:bg-[#C25832] transition-colors duration-300 shadow-[0_12px_30px_rgba(217,107,67,0.35)] flex items-center gap-2"
            >
              Text {BRAND.keyword} to {BRAND.number}
              <ArrowDown size={17} className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <button
              data-testid="hero-cta-how"
              onClick={() => {
                const el = document.querySelector("#manifesto");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              className="font-display font-semibold text-[#1E2022] rounded-full px-7 py-4 text-base border border-[#1E2022]/15 hover:border-[#1E2022]/40 transition-colors duration-300"
            >
              How it works
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="mt-6 text-xs text-[#8A8F98] max-w-md leading-relaxed"
            data-testid="hero-mini-disclosure"
          >
            Msg frequency varies. Msg & data rates may apply. Reply HELP for
            help, STOP to cancel.
          </motion.p>
        </div>

        <motion.div
          style={{ y: phoneY }}
          initial={{ opacity: 0, y: 60, rotate: 4 }}
          animate={{ opacity: 1, y: 0, rotate: 2 }}
          transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="justify-self-center lg:justify-self-end"
        >
          <div className="float-slow">
            <PhoneMockup />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
